import { Component } from "react"

/* Components */
import { Input, Icon } from "../../components"

/* Widgets */
import { toast } from "../common"

/* Sheets */
import FlyerSheet from "./FlyerSheet"

/* Flyer */
import { CANVAS, PRINT_RATIO, FORMATS, THEMES, RULES, VEILS, TYPES, CARDS, BRANDS, INITIAL, dataOf, readOf, fileOf, linkOf, dpiOf } from "./flyer"

/* Constants */
import { env } from "../../constants"

/* HTML to PNG */
import { toPng } from "html-to-image"

/* Download JS */
import download from "downloadjs"

/* HTML to PDF */
import { jsPDF } from "jspdf"


/* The nodes the pictures are taken from */
const FRONT = "flyer-node-front"
const BACK = "flyer-node-back"


/* Widget Flyer Tab — the leaflet of the point. Every stand used to be drawn by hand for one
   point; here the drawing is made once and the colour, the discount, the photo and the texts are
   only chosen. The sheets on the right are the very nodes the pictures are taken from, so what
   the eye sees is what lands in the print */
class FlyerTab extends Component {

    constructor() {
        super()

        const saved = localStorage.getItem("FLYER_FIELDS")

        /* The form survives a reload and a walk between the points — a look picked once is not
           typed in again for every leaflet */
        let fields = { ...INITIAL }

        try {
            fields = saved ? { ...INITIAL, ...JSON.parse(saved) } : { ...INITIAL }
        }
        catch {
            fields = { ...INITIAL }
        }

        this.state = {

            fields,

            /* The photo of the place and the mark of the point, read as the contents of a file */
            photo: null,
            picked: null,
            logo: null,

            exporting: false
        }
    }

    componentDidMount = () => {

        /* The face of the leaflet stands on a photo, so the main photo of the point is taken at
           once — the one the card of the point itself is shown with */
        const images = this.images()

        if (images.length > 0) {
            this.photo(images[0].image)
        }
    }

    /* The photos of the point */
    images = () => {

        const { data } = this.props

        if (!data || !Array.isArray(data.images)) {
            return []
        }

        return data.images
    }

    /* One field of the form */
    set = (key, value) => {
        this.setMany({ [`${key}`]: value })
    }

    /* Several fields at once. Two writes in a row would both read the same old set of the fields
       and the second of them would drop the first, so every change goes through one door */
    setMany = changes => {

        const fields = { ...this.state.fields, ...changes }

        this.setState({ fields })

        localStorage.setItem("FLYER_FIELDS", JSON.stringify(fields))
    }

    /* The ready look. It fills the form and after that every knob turns by hand */
    theme = key => {

        const theme = THEMES.find(item => item.key === key)

        if (!theme) {
            return
        }

        this.setMany({
            theme: key,
            accent: theme.accent,
            band: theme.band,
            veil: theme.veil,
            type: theme.type,
            rule: theme.rule,
            card: theme.card
        })
    }

    /* A colour typed by hand no longer belongs to a look */
    color = (key, value) => {
        this.setMany({ theme: "", [`${key}`]: value })
    }

    /* Take one of the photos of the point under the face. A picture from another address does not
       get into the canvas, so the file is fetched first and only its contents go into the drawing */
    photo = image => {

        return dataOf(`${env.mediapoint}organizations/${image}`).then(photo => {
            this.setState({ photo, picked: image })
        }).catch(() => {
            this.setState({ photo: null, picked: null })
            toast.error("Не удалось загрузить фото точки — выберите свой файл")
        })
    }

    /* A file chosen by hand — the photo of the face or the mark of the point */
    upload = (event, key) => {

        const file = event.target.files && event.target.files.length > 0 ? event.target.files[0] : null

        if (!file) {
            return
        }

        readOf(file).then(data => {

            if (key === "logo") {
                this.setState({ logo: data })
                return
            }

            this.setState({ photo: data, picked: null })
        }).catch(() => toast.error("Не удалось прочитать файл"))
    }

    /* The sheet a leaflet is printed on */
    format = () => {

        const found = FORMATS.find(item => item.key === this.state.fields.format)

        if (found) {
            return found
        }

        return FORMATS[0]
    }

    /* Take the picture of one side. The node is laid out at the full size of the drawing and the
       stage only scales it by the eye, so the picture is asked for at three hundred dots per inch
       and comes out sharp on paper */
    picture = nodeID => {

        const node = document.getElementById(nodeID)

        if (!node) {
            return Promise.reject(new Error("no node"))
        }

        return toPng(node, {
            width: CANVAS.width,
            height: CANVAS.height,
            pixelRatio: PRINT_RATIO,
            cacheBust: true
        })
    }

    /* Download one side as a picture */
    png = side => {

        const { exporting } = this.state
        const { data } = this.props

        if (exporting) {
            return
        }

        this.setState({ exporting: true })

        this.picture(side === "front" ? FRONT : BACK).then(url => {
            download(url, fileOf(data.organization, side === "front" ? "face" : "back", "png"))
            this.setState({ exporting: false })
        }).catch(() => {
            this.setState({ exporting: false })
            toast.error("Не удалось собрать картинку")
        })
    }

    /* Download the whole leaflet — two pages of one document, the face and the back. The printer
       gets one file and not two pictures to put together by hand */
    pdf = () => {

        const { exporting } = this.state
        const { data } = this.props

        if (exporting) {
            return
        }

        this.setState({ exporting: true })

        const format = this.format()

        this.picture(FRONT).then(front => {

            return this.picture(BACK).then(back => {

                const sheet = new jsPDF({ orientation: "portrait", unit: "mm", format: [format.width, format.height] })

                sheet.addImage(front, "PNG", 0, 0, format.width, format.height)
                sheet.addPage([format.width, format.height], "portrait")
                sheet.addImage(back, "PNG", 0, 0, format.width, format.height)

                sheet.save(fileOf(data.organization, "", "pdf"))
                this.setState({ exporting: false })
            })
        }).catch(() => {
            this.setState({ exporting: false })
            toast.error("Не удалось собрать документ")
        })
    }

    /* Draw the photos of the point to choose from */
    _shots = () => {

        const { picked } = this.state
        const images = this.images()

        if (images.length === 0) {
            return <div className="flyer-card-hint">У точки нет ни одной фотографии — выберите свой файл.</div>
        }

        return (
            <div className="flyer-shots">
                {images.map(item =>
                    <div
                        key={`${item.image}`}
                        onClick={() => this.photo(item.image)}
                        className={`flyer-shot ${picked === item.image ? "active" : ""}`}
                    >
                        <img src={`${env.mediapoint}organizations/${item.image}`} alt="Photo" />
                    </div>
                )}
            </div>
        )
    }

    render = () => {

        /* Fields */
        const { fields, photo, logo, exporting } = this.state
        const { data } = this.props

        const organization = data.organization
        const format = this.format()

        /* The stage is narrower than the sheet, so the drawing is scaled by the eye alone */
        const scale = 360 / CANVAS.width

        const sheet = side => (
            <div className="flyer-viewport" style={{ width: CANVAS.width * scale, height: CANVAS.height * scale }}>
                <div className="flyer-scaler" style={{ transform: `scale(${scale})` }}>
                    <FlyerSheet
                        nodeID={side === "front" ? FRONT : BACK}
                        side={side}
                        fields={fields}
                        organization={organization}
                        photo={photo}
                        logo={logo}
                    />
                </div>
            </div>
        )

        return (
            <div className="flyer-tab">

                {/* THE FORM */}
                <div className="flyer-form">

                    {/* THE LOOK */}
                    <div className="flyer-card">

                        <div className="flyer-card-title">Оформление</div>
                        <div className="flyer-card-hint">Готовый вид заполняет форму целиком, дальше каждый переключатель крутится сам по себе.</div>

                        <div className="flyer-themes">
                            {THEMES.map(item =>
                                <div key={item.key} onClick={() => this.theme(item.key)} className={`flyer-theme ${fields.theme === item.key ? "active" : ""}`}>
                                    <div className="flyer-theme-dot" style={{ background: item.accent }} />
                                    {item.text}
                                </div>
                            )}
                        </div>

                        <div className="flyer-colors">

                            <label className="flyer-color">
                                <input type="color" value={fields.accent} onChange={event => this.color("accent", event.target.value)} />
                                <span>Акцент</span>
                                <i>{fields.accent}</i>
                            </label>

                            <label className="flyer-color">
                                <input type="color" value={fields.band} onChange={event => this.color("band", event.target.value)} />
                                <span>Текст плашки</span>
                                <i>{fields.band}</i>
                            </label>

                        </div>

                        <div className="flyer-card-label">Фото под текстом</div>

                        <div className="flyer-chips">
                            {VEILS.map(item =>
                                <div key={item.key} onClick={() => this.set("veil", item.key)} className={`flyer-chip ${fields.veil === item.key ? "active" : ""}`}>
                                    {item.text}
                                </div>
                            )}
                        </div>

                        <div className="flyer-card-label">Заголовки</div>

                        <div className="flyer-chips">
                            {TYPES.map(item =>
                                <div key={item.key} onClick={() => this.set("type", item.key)} className={`flyer-chip ${fields.type === item.key ? "active" : ""}`}>
                                    {item.text}
                                </div>
                            )}
                        </div>

                        <div className="flyer-card-label">Полоса сверху</div>

                        <div className="flyer-chips">
                            {RULES.map(item =>
                                <div key={item.key} onClick={() => this.set("rule", item.key)} className={`flyer-chip ${fields.rule === item.key ? "active" : ""}`}>
                                    {item.text}
                                </div>
                            )}
                        </div>

                        <div className="flyer-card-label">Карточка с кодом</div>

                        <div className="flyer-chips">
                            {CARDS.map(item =>
                                <div key={item.key} onClick={() => this.set("card", item.key)} className={`flyer-chip ${fields.card === item.key ? "active" : ""}`}>
                                    {item.text}
                                </div>
                            )}
                        </div>

                        <div className="flyer-card-label">Формат</div>

                        <div className="flyer-chips">
                            {FORMATS.map(item =>
                                <div key={item.key} onClick={() => this.set("format", item.key)} className={`flyer-chip ${fields.format === item.key ? "active" : ""}`}>
                                    {item.text} <i>{item.hint}</i>
                                </div>
                            )}
                        </div>

                    </div>

                    {/* THE PICTURES */}
                    <div className="flyer-card">

                        <div className="flyer-card-title">Фото и логотип</div>
                        <div className="flyer-card-hint">Фото точки ложится на лицо рефлета. Логотип встаёт в правый угол лица и в подпись на обороте.</div>

                        {this._shots()}

                        <label className="flyer-upload">
                            <input type="file" accept="image/*" onChange={event => this.upload(event, "photo")} />
                            <Icon name="image" size={16} width={1.8} />
                            <span>{photo ? "Заменить фото своим файлом" : "Выбрать фото своим файлом"}</span>
                        </label>

                        <label className="flyer-upload">
                            <input type="file" accept="image/*" onChange={event => this.upload(event, "logo")} />
                            <Icon name="image" size={16} width={1.8} />
                            <span>{logo ? "Заменить логотип точки" : "Выбрать логотип точки"}</span>
                        </label>

                        {logo && <div onClick={() => this.setState({ logo: null })} className="flyer-drop">Убрать файл логотипа</div>}

                        <div className="flyer-card-label">Что стоит в углу</div>

                        <div className="flyer-chips">
                            {BRANDS.map(item =>
                                <div key={item.key} onClick={() => this.set("brand", item.key)} className={`flyer-chip ${fields.brand === item.key ? "active" : ""}`}>
                                    {item.text}
                                </div>
                            )}
                        </div>

                    </div>

                    {/* THE FACE */}
                    <div className="flyer-card">

                        <div className="flyer-card-title">Лицо</div>
                        <div className="flyer-card-hint">В строках со скобками подставляется своё: {"{name}"} — название точки, {"{site}"} — bookify.kz, {"{discount}"} — размер скидки.</div>

                        <div className="flyer-inputs">
                            <Input label="Скидка, %" value={fields.discount} onChange={event => this.set("discount", event.target.value)} />
                            <Input label="Плашка сверху" value={fields.badge} onChange={event => this.set("badge", event.target.value)} />
                        </div>

                        <Input label="Под скидкой" hint="Например: на все услуги барбершопа" value={fields.offer} onChange={event => this.set("offer", event.target.value)} />
                        <Input label="Абзац" textarea rows={3} value={fields.lead} onChange={event => this.set("lead", event.target.value)} />
                        <Input label="Плашка внизу" hint="Например: Онлайн-запись в барбершоп" value={fields.strip} onChange={event => this.set("strip", event.target.value)} />

                    </div>

                    {/* THE BACK */}
                    <div className="flyer-card">

                        <div className="flyer-card-title">Оборот</div>

                        <Input label="Заголовок" hint={`К нему приписывается ${fields.discount}%`} value={fields.how} onChange={event => this.set("how", event.target.value)} />

                        <Input label="Шаг 1" value={fields.first} onChange={event => this.set("first", event.target.value)} />
                        <Input label="Шаг 2" value={fields.second} onChange={event => this.set("second", event.target.value)} />
                        <Input label="Шаг 3" value={fields.third} onChange={event => this.set("third", event.target.value)} />

                        <div className="flyer-inputs">
                            <Input label="Над кодом" value={fields.scanTitle} onChange={event => this.set("scanTitle", event.target.value)} />
                            <Input label="Под кодом" value={fields.scanText} onChange={event => this.set("scanText", event.target.value)} />
                        </div>

                        <Input label="Чем занимается точка" hint="Например: Мужские стрижки · борода · бритьё" value={fields.tagline} onChange={event => this.set("tagline", event.target.value)} />
                        <Input label="Условия мелким шрифтом" textarea rows={3} value={fields.fine} onChange={event => this.set("fine", event.target.value)} />

                        <div className="flyer-chips">
                            <div onClick={() => this.set("withTagline", !fields.withTagline)} className={`flyer-chip ${fields.withTagline ? "active" : ""}`}>Чем занимается</div>
                            <div onClick={() => this.set("withFine", !fields.withFine)} className={`flyer-chip ${fields.withFine ? "active" : ""}`}>Условия</div>
                        </div>

                    </div>

                    {/* THE POINT ITSELF */}
                    <div className="flyer-card">

                        <div className="flyer-card-title">Точка</div>
                        <div className="flyer-card-hint">Пустое поле берёт значение точки. Заполненное — перебивает его.</div>

                        <div className="flyer-inputs">
                            <Input label="Название" hint={organization.name} value={fields.name} onChange={event => this.set("name", event.target.value)} />
                            <Input label="Адрес" hint={organization.address} value={fields.address} onChange={event => this.set("address", event.target.value)} />
                        </div>

                        <Input label="Ссылка кода" hint={`Пусто — ссылка точки: ${linkOf(organization, "")}`} value={fields.link} onChange={event => this.set("link", event.target.value)} />

                        <div className="flyer-chips">
                            <div onClick={() => this.set("withAddress", !fields.withAddress)} className={`flyer-chip ${fields.withAddress ? "active" : ""}`}>Адрес на рефлете</div>
                        </div>

                    </div>

                </div>

                {/* THE STAGE */}
                <div className="flyer-stage">

                    <div className="flyer-stage-inner">

                        <div className="flyer-side-text">Лицо</div>
                        {sheet("front")}

                        <div className="flyer-side-text">Оборот</div>
                        {sheet("back")}

                        <div className="flyer-size">{format.text} · {format.width} × {format.height} мм · {Math.round(CANVAS.width * PRINT_RATIO)} × {Math.round(CANVAS.height * PRINT_RATIO)} px · {dpiOf(CANVAS.width * PRINT_RATIO, format.width)} dpi</div>

                        <div onClick={() => this.pdf()} className={`flyer-button wide ${exporting ? "disabled" : ""}`}>
                            {exporting ? "Собираем..." : "Скачать PDF — обе стороны"}
                        </div>

                        <div className="flyer-buttons">
                            <div onClick={() => this.png("front")} className={`flyer-button grey ${exporting ? "disabled" : ""}`}>Лицо PNG</div>
                            <div onClick={() => this.png("back")} className={`flyer-button grey ${exporting ? "disabled" : ""}`}>Оборот PNG</div>
                        </div>

                        <div className="flyer-note">Рисунок один на весь ряд A, поэтому на меньшем листе та же картинка ложится плотнее. Фото и логотип читаются только из файла: ссылку на чужой адрес браузер в картинку не пустит.</div>

                    </div>

                </div>

            </div>
        )
    }
}

export default FlyerTab
